import { z } from "zod";
import {
  AccountRoleSchema,
  BusinessRoleSchema,
  EndUserRoleSchema,
} from "./auth.schema";
import { optionalNamed, optionalUuid } from "./_helpers/optional";
import { clampedLimit } from "./common.schema";

// ─── User Status ─────────────────────────────────────────────────────────────

export const UserStatusSchema = z.enum(["active", "inactive", "invited"]);
export type UserStatus = z.infer<typeof UserStatusSchema>;

// ─── Invite User ─────────────────────────────────────────────────────────────

export const InviteUserSchema = z.object({
  email: z.string().email().describe("Email"),
  first_name: z.string().optional().describe("First Name"),
  last_name: z.string().optional().describe("Last Name"),
  account_role: AccountRoleSchema.exclude(["super_admin"]).describe(
    "Account Role",
  ),
  business_ids: z
    .array(z.string().uuid())
    .optional()
    .describe("Business Assignments"),
  business_role: optionalNamed(BusinessRoleSchema).describe("Business Role"),
  end_user_role: EndUserRoleSchema.nullable()
    .optional()
    .describe("End User Role"),
  // ADV-387: when set, skip Supabase Auth invite and insert account_users with
  // the supplied auth.users id. Super-admin force-link path from the duplicate
  // email dialog — resolve the id via the lookup endpoint first.
  force_link_existing_auth_user_id: optionalUuid().describe(
    "Link Existing Auth User",
  ),
});
export type InviteUser = z.infer<typeof InviteUserSchema>;

// ─── Update User ─────────────────────────────────────────────────────────────

export const UpdateUserSchema = z.object({
  account_role: optionalNamed(
    AccountRoleSchema.exclude(["super_admin"]),
  ).describe("Account Role"),
  business_ids: z
    .array(z.string().uuid())
    .optional()
    .describe("Business Assignments"),
  business_role: optionalNamed(BusinessRoleSchema).describe("Business Role"),
  status: optionalNamed(UserStatusSchema.exclude(["invited"])).describe(
    "Status",
  ),
});
export type UpdateUser = z.infer<typeof UpdateUserSchema>;

// ─── User Business Assignment (response shape) ──────────────────────────────

export const UserBusinessAssignmentSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  business_name: z.string().nullable().optional().describe("Business Name"),
  role: BusinessRoleSchema.nullable().describe("Business Role"),
  end_user_role: EndUserRoleSchema.nullable()
    .optional()
    .describe("End User Role"),
});
export type UserBusinessAssignment = z.infer<
  typeof UserBusinessAssignmentSchema
>;

// ─── User Response ───────────────────────────────────────────────────────────

export const UserResponseSchema = z.object({
  id: z.string().uuid().describe("ID"),
  email: z.string().email().describe("Email"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  phone: z.string().nullable().describe("Phone"),
  account_role: AccountRoleSchema.describe("Account Role"),
  status: UserStatusSchema.describe("Status"),
  all_businesses_access: z.boolean().describe("All Businesses Access"),
  invited_at: z.string().nullable().describe("Invited At"),
  accepted_at: z.string().nullable().describe("Accepted At"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  businesses: z.array(UserBusinessAssignmentSchema).describe("Businesses"),
});
export type UserResponse = z.infer<typeof UserResponseSchema>;

// ─── List Users Response ─────────────────────────────────────────────────────

export const ListUsersResponseSchema = z.object({
  users: z.array(UserResponseSchema).describe("Users"),
  total: z.number().describe("Total"),
});
export type ListUsersResponse = z.infer<typeof ListUsersResponseSchema>;

// ─── List Users Query ────────────────────────────────────────────────────────

export const ListUsersQuerySchema = z.object({
  q: z.string().optional().describe("Search"),
  status: UserStatusSchema.optional().describe("Status"),
  role: AccountRoleSchema.exclude(["super_admin"]).optional().describe("Role"),
  limit: clampedLimit(100, 50).describe("Limit"),
  offset: z.coerce.number().min(0).default(0).describe("Offset"),
});
export type ListUsersQuery = z.infer<typeof ListUsersQuerySchema>;

// ─── Admin (cross-account) schemas ───────────────────────────────────────────

export const AdminUserResponseSchema = UserResponseSchema.extend({
  account_id: z.string().uuid().describe("Account ID"),
  account_name: z.string().describe("Account Name"),
  last_sign_in_at: z.string().nullable().describe("Last Sign In At"),
});
export type AdminUserResponse = z.infer<typeof AdminUserResponseSchema>;

export const AdminListUsersResponseSchema = z.object({
  users: z.array(AdminUserResponseSchema).describe("Users"),
  total: z.number().describe("Total"),
});
export type AdminListUsersResponse = z.infer<
  typeof AdminListUsersResponseSchema
>;

export const AdminListUsersQuerySchema = z.object({
  q: z.string().optional().describe("Search"),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  account_id: z.string().uuid().optional().describe("Account Filter"),
  status: UserStatusSchema.optional().describe("Status"),
  role: AccountRoleSchema.exclude(["super_admin"]).optional().describe("Role"),
  limit: clampedLimit(100, 50).describe("Limit"),
  offset: z.coerce.number().min(0).default(0).describe("Offset"),
});
export type AdminListUsersQuery = z.infer<typeof AdminListUsersQuerySchema>;

export const AdminStatsResponseSchema = z.object({
  account_count: z.number().int().nonnegative().describe("Account Count"),
  user_count: z.number().int().nonnegative().describe("User Count"),
  business_count: z.number().int().nonnegative().describe("Business Count"),
});
export type AdminStatsResponse = z.infer<typeof AdminStatsResponseSchema>;

// ─── Auth user lookup (ADV-387) ──────────────────────────────────────────────
// Used by the super-admin duplicate-email dialog on account create / invite
// flows. Returns the auth.users row (if any) plus every existing Adva account
// membership for that user so the UI can show "this email is already on X and
// Y" before the super admin chooses to link-vs-invite.

export const LookupAuthUserQuerySchema = z.object({
  email: z.string().email().describe("Email"),
});
export type LookupAuthUserQuery = z.infer<typeof LookupAuthUserQuerySchema>;

export const AuthUserMembershipSchema = z.object({
  account_user_id: z.string().uuid().describe("Account User ID"),
  account_id: z.string().uuid().describe("Account ID"),
  account_name: z.string().describe("Account Name"),
  role: z.string().describe("Role"),
  status: z.string().describe("Status"),
});
export type AuthUserMembership = z.infer<typeof AuthUserMembershipSchema>;

export const LookupAuthUserResponseSchema = z.object({
  authUser: z
    .object({
      id: z.string().uuid().describe("Auth User ID"),
      email: z.string().email().describe("Email"),
      email_confirmed: z.boolean().describe("Email Confirmed"),
    })
    .nullable()
    .describe("Auth User"),
  memberships: z.array(AuthUserMembershipSchema).describe("Memberships"),
});
export type LookupAuthUserResponse = z.infer<
  typeof LookupAuthUserResponseSchema
>;

// ─── Orphaned Auth Users (super_admin only, ADV-384) ────────────────────────

export const OrphanedAuthUserSchema = z.object({
  auth_user_id: z.string().uuid().describe("Auth User ID"),
  email: z.string().email().describe("Email"),
  email_confirmed: z.boolean().describe("Email Confirmed"),
  last_sign_in_at: z.string().nullable().describe("Last Sign In At"),
  created_at: z.string().describe("Created At"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
});
export type OrphanedAuthUser = z.infer<typeof OrphanedAuthUserSchema>;

export const OrphanedAuthUsersResponseSchema = z.object({
  users: z.array(OrphanedAuthUserSchema).describe("Users"),
  total: z.number().int().nonnegative().describe("Total"),
});
export type OrphanedAuthUsersResponse = z.infer<
  typeof OrphanedAuthUsersResponseSchema
>;

// ─── Force-Link Action Response (ADV-384) ────────────────────────────────────

export const ForceLinkUserResponseSchema = z.object({
  linked: z.boolean().describe("Linked"),
  auth_user_id: z.string().uuid().nullable().describe("Auth User ID"),
  promoted_to_active: z.boolean().describe("Promoted To Active"),
  message: z.string().describe("Message"),
});
export type ForceLinkUserResponse = z.infer<typeof ForceLinkUserResponseSchema>;

export const ResendInviteResponseSchema = z.object({
  message: z.string().describe("Message"),
  email: z.string().email().describe("Email"),
});
export type ResendInviteResponse = z.infer<typeof ResendInviteResponseSchema>;

// ─── Owner-scoped Account User Management (ADV-388, renamed in ADV-806) ─────
// Customer-facing surface at /settings/team backing /api/v1/core/account/users
// (renamed from /core/team in ADV-806 — the old path read like it returned
// team_member contacts when it actually returns account_users login rows).
// Distinct from the full /core/users endpoints because:
//   - Role on invite is locked server-side to `admin` (owners cannot mint
//     other owners — protects the single-owner invariant the ticket specifies)
//   - Remove is gated against self + other-owners at the service layer
//   - Response includes last_sign_in_at so owners can see who's active without
//     needing the super-admin cross-account view

export const OwnerInviteAdminSchema = z.object({
  email: z.string().email().describe("Email"),
  first_name: z.string().optional().describe("First Name"),
  last_name: z.string().optional().describe("Last Name"),
  // ADV-387 parity: when the owner confirms a duplicate-email dialog and
  // chooses "link existing user", resolve the auth.users id via
  // /core/users/lookup-auth-by-email first and pass it here. Omitted for the
  // normal new-user invite path.
  force_link_existing_auth_user_id: optionalUuid().describe(
    "Link Existing Auth User",
  ),
});
export type OwnerInviteAdmin = z.infer<typeof OwnerInviteAdminSchema>;

export const AccountUserResponseSchema = UserResponseSchema.extend({
  last_sign_in_at: z.string().nullable().describe("Last Sign In At"),
});
export type AccountUserResponse = z.infer<typeof AccountUserResponseSchema>;

export const ListAccountUsersResponseSchema = z.object({
  members: z.array(AccountUserResponseSchema).describe("Members"),
  total: z.number().int().nonnegative().describe("Total"),
});
export type ListAccountUsersResponse = z.infer<
  typeof ListAccountUsersResponseSchema
>;

// ─── Account-scoped user list (super_admin only, ADV-389) ───────────────────
// Superset of AdminUserResponseSchema for the per-account drill-down. Drops
// account_name (caller already knows the account) and adds is_stale_invite,
// computed server-side as status='invited' AND invited_at older than 7 days.

export const AccountUserOnAccountSchema = AdminUserResponseSchema.omit({
  account_name: true,
}).extend({
  is_stale_invite: z.boolean().describe("Is Stale Invite"),
});
export type AccountUserOnAccount = z.infer<typeof AccountUserOnAccountSchema>;

export const AccountUsersListResponseSchema = z.object({
  users: z.array(AccountUserOnAccountSchema).describe("Users"),
  total: z.number().int().nonnegative().describe("Total"),
});
export type AccountUsersListResponse = z.infer<
  typeof AccountUsersListResponseSchema
>;

export const RemoveUserResponseSchema = z.object({
  message: z.string().describe("Message"),
});
export type RemoveUserResponse = z.infer<typeof RemoveUserResponseSchema>;

// ─── Business Member Management (ADV-622) ───────────────────────────────────

export const InviteBusinessMemberSchema = z.object({
  email: z.string().email().describe("Email"),
  role: BusinessRoleSchema.describe("Business Role"),
});
export type InviteBusinessMember = z.infer<typeof InviteBusinessMemberSchema>;

export const InviteBusinessMemberResponseSchema = z.object({
  action: z
    .enum(["invited_new", "added_existing", "already_member"])
    .describe("Action"),
  message: z.string().describe("Message"),
});
export type InviteBusinessMemberResponse = z.infer<
  typeof InviteBusinessMemberResponseSchema
>;

export const BusinessMemberSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_user_id: z.string().uuid().describe("Account User"),
  email: z.string().email().describe("Email"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  role: BusinessRoleSchema.nullable().describe("Business Role"),
  account_role: AccountRoleSchema.describe("Account Role"),
  status: UserStatusSchema.describe("Status"),
  invited_at: z.string().nullable().describe("Invited At"),
  accepted_at: z.string().nullable().describe("Accepted At"),
});
export type BusinessMember = z.infer<typeof BusinessMemberSchema>;

export const ListBusinessMembersResponseSchema = z.object({
  members: z.array(BusinessMemberSchema).describe("Members"),
  total: z.number().int().nonnegative().describe("Total"),
});
export type ListBusinessMembersResponse = z.infer<
  typeof ListBusinessMembersResponseSchema
>;

// ─── Admin User Detail (ADV-623) ────────────────────────────────────────────

export const AdminUserDetailsSchema = AdminUserResponseSchema.extend({
  auth_user_id: z.string().uuid().nullable().describe("Auth User ID"),
  email_confirmed_at: z
    .string()
    .datetime()
    .nullable()
    .describe("Email Confirmed At"),
  banned: z.boolean().describe("Suspended"),
  business_assignments: z
    .array(
      z.object({
        business_id: z.string().uuid().describe("Business"),
        business_name: z.string().describe("Business Name"),
        role: BusinessRoleSchema.nullable().describe("Role"),
      }),
    )
    .describe("Business Assignments"),
});
export type AdminUserDetails = z.infer<typeof AdminUserDetailsSchema>;

// ─── Admin Update User Role (ADV-623) ───────────────────────────────────────

export const AdminUpdateUserRoleSchema = z.object({
  account_role: AccountRoleSchema.exclude(["super_admin"])
    .optional()
    .describe("Account Role"),
  business_assignments: z
    .array(
      z.object({
        business_id: z.string().uuid().describe("Business"),
        role: BusinessRoleSchema.describe("Role"),
        end_user_role: EndUserRoleSchema.nullable()
          .optional()
          .describe("End User Role"),
      }),
    )
    .optional()
    .describe("Business Assignments"),
});
export type AdminUpdateUserRole = z.infer<typeof AdminUpdateUserRoleSchema>;

// ─── Admin Reset Password Response (ADV-623) ────────────────────────────────

export const AdminResetPasswordResponseSchema = z.object({
  message: z.string().describe("Message"),
  link_sent: z.boolean().describe("Link Sent"),
});
export type AdminResetPasswordResponse = z.infer<
  typeof AdminResetPasswordResponseSchema
>;

// ─── Admin Suspend User Response (ADV-623) ──────────────────────────────────

export const AdminSuspendUserResponseSchema = z.object({
  message: z.string().describe("Message"),
  suspended: z.boolean().describe("Suspended"),
});
export type AdminSuspendUserResponse = z.infer<
  typeof AdminSuspendUserResponseSchema
>;
